import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Calculator,
  Check,
  ListChecks,
  Microscope,
  BookOpen,
  GraduationCap,
  Search,
  MessageSquare,
  FileText,
  ArrowRight,
  type Icon
} from 'lucide-react';
import SolutionCard from '../../components/ui/SolutionCard';

interface Solution {
  icon: Icon;
  title: string;
  price: string;
  description: string;
  features: string[];
}

const SolutionsPage: React.FC = () => {
  const solutions: Solution[] = [
    {
      icon: Calculator,
      title: 'AFS Review - Casts',
      price: '$0.33 per use',
      description: 'Verify financial calculations and casts in financial statements.',
      features: [
        'Addition and subtraction verification',
        'Cross-statement consistency checks',
        'Ratio and percentage validation'
      ]
    },
    {
      icon: ListChecks,
      title: 'AFS Review - Disclosure',
      price: '$0.65 per use',
      description: 'Prepare comprehensive disclosure checklists and verify compliance.',
      features: [
        'IFRS disclosure requirements',
        'Regulatory compliance verification',
        'Missing disclosure identification'
      ]
    },
    {
      icon: Microscope,
      title: 'AFS Review - Other',
      price: '$0.80 per use',
      description: 'Additional comprehensive financial checks for specialized requirements.',
      features: [
        'Industry-specific compliance',
        'Advanced analytical procedures',
        'Custom validation rules'
      ]
    },
    {
      icon: BookOpen,
      title: 'IFRS Specialist',
      price: '$0.60 per use',
      description: 'Expert guidance on complex IFRS queries and technical position papers.',
      features: [
        'IFRS standard interpretations',
        'Implementation guidance',
        'Technical position papers'
      ]
    },
    {
      icon: GraduationCap,
      title: 'Audit Genius',
      price: '$0.60 per use',
      description: 'AI-powered analysis of audit procedures and working papers.',
      features: [
        'ISA compliance guidance',
        'Audit methodology advice',
        'Risk assessment support'
      ]
    },
    {
      icon: Search,
      title: 'Media Analyzer',
      price: '$0.90 per use',
      description: 'Analyze company media coverage for audit-relevant information.',
      features: [
        'News sentiment analysis',
        'Regulatory filing monitoring',
        'Risk factor identification'
      ]
    },
    {
      icon: FileText,
      title: 'Audit Working Paper Reviewer',
      price: '$1.20 per use',
      description: 'Review and analyze audit working papers based on ISA standards.',
      features: [
        'Documentation completeness check',
        'Evidence sufficiency assessment',
        'Conclusion validation'
      ]
    },
    {
      icon: MessageSquare,
      title: 'Client Meeting Analyzer',
      price: '$1.25 per use',
      description: 'Analyze and summarize client meetings and discussions.',
      features: [
        'Key point extraction',
        'Action item identification',
        'Risk factor highlighting'
      ]
    }
  ];

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-cyan-600 to-blue-700">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-r from-cyan-600 to-blue-700 mix-blend-multiply" />
        </div>
        <div className="relative max-w-7xl mx-auto py-24 px-4 sm:py-32 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl">
            AI Solutions & Pricing
          </h1>
          <p className="mt-6 max-w-3xl text-xl text-cyan-100">
            Explore our comprehensive suite of AI-powered tools designed for financial professionals.
          </p>
        </div>
      </div>

      {/* Solutions Grid */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-base font-semibold text-cyan-600 tracking-wide uppercase">Our Solutions</h2>
            <p className="mt-2 text-3xl font-extrabold text-gray-900 sm:text-4xl">
              Comprehensive AI Tools for Financial Professionals
            </p>
            <p className="mt-4 max-w-2xl mx-auto text-xl text-gray-500">
              Our AI-powered solutions streamline financial reporting and auditing processes, enhancing efficiency and accuracy.
            </p>
          </div>

          <div className="mt-16 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {solutions.map((solution, index) => (
              <SolutionCard key={index} {...solution} />
            ))}
          </div>

          {/* CTA Section */}
          <div className="mt-16 bg-gradient-to-r from-cyan-600 to-blue-700 rounded-xl shadow-lg overflow-hidden">
            <div className="px-6 py-8 sm:p-10">
              <h2 className="text-2xl font-bold text-white mb-4">
                Ready to enhance your financial reporting workflow?
              </h2>
              <p className="text-lg text-cyan-100 mb-8">
                Get started with Prestige AI today and experience the power of AI-driven automation.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
                <Link
                  to="/register"
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-cyan-700 bg-white hover:bg-cyan-50"
                >
                  Sign up now
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
                <Link
                  to="/about"
                  className="inline-flex items-center px-6 py-3 border border-white text-base font-medium rounded-md text-white hover:bg-cyan-700"
                >
                  Learn more
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SolutionsPage;